import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Checkbox,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Nota,
  PageHeader,
  Recolhivel,
  SearchInput,
  Select,
  Skeleton,
  Tabs,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';

import { TI, TIBIKE, TIBIKE_NOVO } from '../routes';
import { numeroParaMoeda } from '../../../utils/moeda';
import { extrairMensagemErro } from '../../../utils/apiError';
import {
  BikeEletrica,
  BikeMarca,
  atualizarBikeEletrica,
  atualizarBikeMarca,
  criarBikeMarca,
  listarBikeMarcas,
  listarBikesEletricas,
} from '../../../api/bike-eletrica/bikeEletricaApi';
import {
  fichaTecnicaResumida,
  marcasSemModeloQueCasam,
  rotuloCodigoSga,
  rotuloOrigemBike,
  rotuloSituacaoBike,
} from '../../../utils/bikeRegras';

/**
 * Catálogo de motocicletas elétricas (at-45/at-46, item 50; reescrito na at-69).
 *
 * 📛 O produto se chama **motocicleta elétrica** na interface desde a at-54 (item
 * 57) — é o tipo `35 — MOTOCICLETA - ELÉTRICA` do SGA. Os identificadores seguem
 * com "bike" de propósito (`BikeEletrica`, `/ti/bikes`, prefixo de placa `BIK`):
 * renomeá-los custaria migration e refactor sem o usuário ver diferença.
 *
 * ## O que mudou na at-69
 *
 *   - **Marcas viraram ABA** (A4). O painel embutido abria entre a barra e a
 *     tabela e empurrava tudo para baixo; como aba ganha lista própria, contador
 *     no rótulo e as mesmas ações.
 *   - **Erro deixou de ser mudo**: era `toast` + "Nenhuma motocicleta elétrica
 *     cadastrada." — o TI concluía que o catálogo estava vazio.
 *   - **O `FullScreenLoader` a cada tecla** da busca virou `Skeleton` no lugar da
 *     lista (a busca é debounced, então o overlay piscava enquanto se digitava).
 *   - **As 4 explicações que eram `title` viraram texto** (at-65).
 *   - **O editor virou ficha em sub-rota** (`/ti/bikes/novo` e `/ti/bikes/:id`).
 *
 * ⚠️ **O vazio especial foi PRESERVADO**: a lista mostra MODELOS, então buscar por
 * marca recém-criada devolve vazio — correto e indistinguível de "a busca
 * quebrou". A regra saiu para `utils/bikeRegras.ts`, onde a lista e o vazio a
 * dividem.
 */

type Estado = 'carregando' | 'ok' | 'erro';
type Aba = 'modelos' | 'marcas';

type Confirmacao =
  | { tipo: 'INATIVAR_BIKE' | 'REATIVAR_BIKE'; id: string; rotulo: string }
  | { tipo: 'INATIVAR_MARCA' | 'REATIVAR_MARCA'; id: string; rotulo: string };

const ID_BASE = 'bikes';

const BikesEletricasPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TI());

  const [bikes, setBikes] = useState<BikeEletrica[]>([]);
  const [marcas, setMarcas] = useState<BikeMarca[]>([]);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  const [aba, setAba] = useState<Aba>('modelos');
  const [busca, setBusca] = useState('');
  const [marcaFiltro, setMarcaFiltro] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [chaveBusca, setChaveBusca] = useState(0);

  const [novaMarca, setNovaMarca] = useState('');
  const [criandoMarca, setCriandoMarca] = useState(false);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const [listaBikes, listaMarcas] = await Promise.all([
        listarBikesEletricas({
          busca: busca || undefined,
          marcaId: marcaFiltro || undefined,
          incluirInativos: mostrarInativos,
        }),
        // As marcas vêm sempre completas (inclusive inativas): a tela precisa
        // delas para o filtro e para a aba de gestão.
        listarBikeMarcas({ incluirInativas: true }),
      ]);
      setBikes(listaBikes);
      setMarcas(listaMarcas);
      setEstado('ok');
    } catch (falha) {
      setBikes([]);
      setMarcas([]);
      setErro(
        extrairMensagemErro(falha, 'Não foi possível carregar o catálogo de motocicletas elétricas.'),
      );
      setEstado('erro');
    }
  }, [busca, marcaFiltro, mostrarInativos]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const criarMarca = async () => {
    const nome = novaMarca.trim();
    if (nome === '' || criandoMarca) return;

    setCriandoMarca(true);
    try {
      await criarBikeMarca(nome);
      toast.success(`Marca "${nome}" cadastrada`);
      setNovaMarca('');
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Erro ao cadastrar a marca'));
    } finally {
      setCriandoMarca(false);
    }
  };

  const confirmar = async () => {
    if (!confirmacao || processando) return;

    setProcessando(true);
    try {
      if (confirmacao.tipo === 'INATIVAR_BIKE' || confirmacao.tipo === 'REATIVAR_BIKE') {
        await atualizarBikeEletrica(confirmacao.id, {
          ativo: confirmacao.tipo === 'REATIVAR_BIKE',
        });
        toast.success(
          confirmacao.tipo === 'REATIVAR_BIKE'
            ? 'Motocicleta elétrica reativada'
            : 'Motocicleta elétrica inativada',
        );
      } else {
        await atualizarBikeMarca(confirmacao.id, {
          situacao: confirmacao.tipo === 'REATIVAR_MARCA' ? 'ATIVO' : 'INATIVO',
        });
        toast.success(
          confirmacao.tipo === 'REATIVAR_MARCA' ? 'Marca reativada' : 'Marca inativada',
        );
      }
      setConfirmacao(null);
      await carregar();
    } catch (falha) {
      // O 400 de "marca com modelos ativos" chega com a mensagem do backend, que
      // já explica o que fazer — não reescrever a orientação aqui.
      toast.error(extrairMensagemErro(falha, 'Não foi possível concluir a ação'));
    } finally {
      setProcessando(false);
    }
  };

  const novoModeloDaMarca = (marcaId: string) =>
    navigate(`${TIBIKE_NOVO()}?marca=${encodeURIComponent(marcaId)}`);

  const seeds = bikes.filter((b) => b.origem === 'SEED').length;
  /* at-46: modelo ativo sem código FIPE do SGA = adesão que não sobe ao SGA. */
  const semCodigoSga = bikes.filter((b) => b.ativo && !b.codigoFipeSga).length;
  const semModeloNaBusca = marcasSemModeloQueCasam(marcas, busca);

  const textoConfirmacao = (): { titulo: string; mensagem: string; detalhe?: string } => {
    if (!confirmacao) return { titulo: '', mensagem: '' };

    switch (confirmacao.tipo) {
      case 'INATIVAR_BIKE':
        return {
          titulo: 'Inativar motocicleta elétrica',
          mensagem: `Inativar "${confirmacao.rotulo}"?`,
          detalhe:
            'Ela deixa de aparecer para seleção na adesão. O registro não é apagado e pode ser reativado.',
        };
      case 'REATIVAR_BIKE':
        return {
          titulo: 'Reativar motocicleta elétrica',
          mensagem: `Reativar "${confirmacao.rotulo}"?`,
          detalhe: 'Ela volta a aparecer para seleção na adesão.',
        };
      case 'INATIVAR_MARCA':
        return {
          titulo: 'Inativar marca',
          mensagem: `Inativar a marca "${confirmacao.rotulo}"?`,
          detalhe:
            'Marcas com modelos ativos não podem ser inativadas — inative os modelos antes.',
        };
      default:
        return {
          titulo: 'Reativar marca',
          mensagem: `Reativar a marca "${confirmacao.rotulo}"?`,
        };
    }
  };

  const conf = textoConfirmacao();

  const colunasModelos = [
    {
      id: 'marca',
      rotulo: 'Marca',
      secundaria: true,
      celula: (b: BikeEletrica) => b.marca,
    },
    {
      id: 'modelo',
      rotulo: 'Modelo',
      principal: true,
      celula: (b: BikeEletrica) => (
        <span>
          {b.modelo}
          {/* ⚠️ "Ciclomotor" é classificação LEGAL (CONTRAN 996/2023), não o nome
              comercial do produto — a at-54 proíbe renomeá-la. */}
          {b.tipoLegal === 'CICLOMOTOR' && (
            <>
              {' '}
              <Chip variante="neutro">ciclomotor</Chip>
            </>
          )}
        </span>
      ),
    },
    {
      id: 'valor',
      rotulo: 'Valor de referência',
      celula: (b: BikeEletrica) => {
        const carga = rotuloOrigemBike(b.origem);
        return (
          <span>
            {numeroParaMoeda(b.valorReferencia)}
            {carga && (
              <>
                {' '}
                <Chip variante={carga.variante}>{carga.texto}</Chip>
              </>
            )}
          </span>
        );
      },
    },
    {
      id: 'sga',
      rotulo: 'Código no SGA',
      celula: (b: BikeEletrica) => {
        const codigo = rotuloCodigoSga(b.codigoFipeSga);
        return <Chip variante={codigo.variante}>{codigo.texto}</Chip>;
      },
    },
    {
      id: 'ficha',
      rotulo: 'Ficha',
      ocultarNoCard: true,
      celula: (b: BikeEletrica) => fichaTecnicaResumida(b),
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (b: BikeEletrica) => {
        const situacao = rotuloSituacaoBike(b.ativo);
        return <Chip variante={situacao.variante}>{situacao.texto}</Chip>;
      },
    },
  ];

  const colunasMarcas = [
    {
      id: 'nome',
      rotulo: 'Nome',
      principal: true,
      celula: (m: BikeMarca) => (
        <span>
          {m.nome}
          {m.situacao !== 'ATIVO' && (
            <>
              {' '}
              <Chip variante="neutro">Inativa</Chip>
            </>
          )}
        </span>
      ),
    },
    {
      id: 'modelos',
      rotulo: 'Modelos',
      celula: (m: BikeMarca) => `${m.totalModelos} modelo(s)`,
    },
  ];

  const painelModelos = () => {
    if (estado === 'carregando') return <Skeleton linhas={5} altura="tw-h-14" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o catálogo."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (bikes.length === 0) {
      /* ⚠️ O vazio ESPECIAL: a lista mostra modelos, e marca recém-criada não
         aparece aqui até ganhar o primeiro. Dizer isso (e oferecer o atalho) é o
         que evita a leitura de que a busca está quebrada (at-45). */
      if (semModeloNaBusca.length > 0) {
        return (
          <EmptyState
            icone="busca"
            titulo={
              semModeloNaBusca.length === 1
                ? `A marca ${semModeloNaBusca[0].nome} ainda não tem nenhum modelo`
                : 'Estas marcas ainda não têm modelos cadastrados'
            }
            descricao={
              semModeloNaBusca.length === 1
                ? 'Ela existe no catálogo, mas esta lista mostra modelos — e ela ainda não tem nenhum.'
                : `Existem no catálogo, mas ainda sem modelos: ${semModeloNaBusca
                    .map((m) => m.nome)
                    .join(', ')}.`
            }
            acao={
              semModeloNaBusca.length === 1 ? (
                <Botao onClick={() => novoModeloDaMarca(semModeloNaBusca[0].id)}>
                  {`Cadastrar modelo em ${semModeloNaBusca[0].nome}`}
                </Botao>
              ) : undefined
            }
          />
        );
      }

      if (busca || marcaFiltro) {
        return (
          <EmptyState
            icone="busca"
            titulo="Nenhuma motocicleta elétrica para esse recorte"
            descricao="Nada corresponde à busca ou ao filtro de marca escolhido."
            acao={
              <Botao
                variante="secundario"
                onClick={() => {
                  setBusca('');
                  setMarcaFiltro('');
                  setChaveBusca((c) => c + 1);
                }}
              >
                Limpar busca e filtro
              </Botao>
            }
          />
        );
      }

      return (
        <EmptyState
          icone="sem-dados"
          titulo="Nenhuma motocicleta elétrica cadastrada"
          descricao="Cadastre o primeiro modelo para que ele fique disponível na adesão."
          acao={<Botao onClick={() => navigate(TIBIKE_NOVO())}>Nova motocicleta elétrica</Botao>}
        />
      );
    }

    return (
      <DataList
        itens={bikes}
        colunas={colunasModelos}
        chave={(b) => b.id}
        rotuloAcessivel="Modelos de motocicleta elétrica"
        acoes={(b) => (
          <>
            <Botao variante="secundario" tamanho="sm" onClick={() => navigate(TIBIKE(b.id))}>
              Editar
            </Botao>
            <Botao
              variante="secundario"
              tamanho="sm"
              onClick={() =>
                setConfirmacao({
                  tipo: b.ativo ? 'INATIVAR_BIKE' : 'REATIVAR_BIKE',
                  id: b.id,
                  rotulo: `${b.marca} ${b.modelo}`,
                })
              }
            >
              {b.ativo ? 'Inativar' : 'Reativar'}
            </Botao>
          </>
        )}
      />
    );
  };

  const painelMarcas = () => (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Nota>
        A marca vem de uma lista <strong>própria</strong>: o espelho do SGA só tem fabricantes de
        veículo automotor. Marca com modelos ativos <strong>não pode ser inativada</strong> —
        inative os modelos antes.
      </Nota>

      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
        <Field rotulo="Nome da nova marca" className="tw-min-w-0 tw-flex-1 tw-basis-64" campo="novaMarca">
          <Input
            value={novaMarca}
            maxLength={80}
            placeholder="Ex.: Caloi"
            onChange={(evento) => setNovaMarca(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') void criarMarca();
            }}
          />
        </Field>

        <Botao
          iconeEsquerda="mais"
          disabled={novaMarca.trim() === ''}
          carregando={criandoMarca}
          onClick={() => void criarMarca()}
        >
          Adicionar
        </Botao>
      </div>

      {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-12" />}

      {estado === 'erro' && (
        <ErrorState
          mensagem="Não foi possível carregar as marcas."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      )}

      {estado === 'ok' &&
        (marcas.length === 0 ? (
          <EmptyState
            icone="sem-dados"
            titulo="Nenhuma marca cadastrada"
            descricao="Cadastre a primeira marca acima para poder cadastrar modelos."
          />
        ) : (
          <DataList
            itens={marcas}
            colunas={colunasMarcas}
            chave={(m) => m.id}
            rotuloAcessivel="Marcas de motocicleta elétrica"
            acoes={(m) => (
              <>
                {/* Sem isto, criar uma marca não leva a lugar nenhum: ela entra na
                    lista e o TI precisa descobrir sozinho que o próximo passo é
                    "Nova motocicleta elétrica" e reencontrá-la no seletor. */}
                {m.situacao === 'ATIVO' && (
                  <Botao variante="secundario" tamanho="sm" onClick={() => novoModeloDaMarca(m.id)}>
                    + Modelo
                  </Botao>
                )}
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  onClick={() =>
                    setConfirmacao({
                      tipo: m.situacao === 'ATIVO' ? 'INATIVAR_MARCA' : 'REATIVAR_MARCA',
                      id: m.id,
                      rotulo: m.nome,
                    })
                  }
                >
                  {m.situacao === 'ATIVO' ? 'Inativar' : 'Reativar'}
                </Botao>
              </>
            )}
          />
        ))}
    </div>
  );

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Motocicletas Elétricas"
          sobretitulo="Administração (TI)"
          aoVoltar={voltar}
          acaoDireita={
            <Botao tamanho="md" iconeEsquerda="mais" onClick={() => navigate(TIBIKE_NOVO())}>
              Nova motocicleta elétrica
            </Botao>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Tabs
                idBase={ID_BASE}
                abas={[
                  { id: 'modelos', rotulo: 'Modelos' },
                  /* O contador só aparece com dados carregados: "Marcas (0)"
                     durante a carga afirmaria justamente o que não se sabe (at-66). */
                  { id: 'marcas', rotulo: estado === 'ok' ? `Marcas (${marcas.length})` : 'Marcas' },
                ]}
                ativa={aba}
                aoTrocar={(valor) => setAba(valor as Aba)}
                rotuloAcessivel="Seções do catálogo"
              />

              <div
                id={idDoPainel(ID_BASE)}
                role="tabpanel"
                aria-labelledby={idDaAba(ID_BASE, aba)}
                tabIndex={-1}
                className="tw-flex tw-flex-col tw-gap-4"
              >
                {aba === 'modelos' ? (
                  <>
                    {/* Texto preservado da tela antiga (D8). */}
                    <Nota>
                      Motocicleta elétrica <strong>não tem tabela FIPE</strong>. O valor cadastrado
                      aqui é uma <strong>referência</strong>: na adesão ele pré-carrega o{' '}
                      <strong>Valor Protegido</strong>, e é esse valor — que o consultor pode
                      ajustar — que define cota, rateio e produtos.
                    </Nota>

                    {/* O texto do fluxo do código fictício é longo e o TI já o
                        conhece; recolhido, ele continua na tela sem empurrar a
                        lista (pedido de Victor na at-46: a explicação vive aqui). */}
                    <Recolhivel titulo="Como a motocicleta elétrica sobe ao SGA">
                      <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-texto-corpo">
                        <strong>Para a motocicleta elétrica subir ao SGA</strong>, cada modelo
                        precisa de um <strong>código FIPE fictício</strong>: o SGA exige que todo
                        veículo aponte para um código do catálogo dele. O fluxo é o mesmo que a
                        operação já usa no SGA Consultor — (1) cadastre o modelo{' '}
                        <strong>na tela do SGA</strong>, (2) copie o código FIPE que o SGA gerou,
                        (3) cole no cadastro do modelo aqui. Modelo <strong>sem código</strong>{' '}
                        fecha a adesão normalmente no SAAC, mas o envio ao SGA fica{' '}
                        <strong>pendente</strong> até o código ser informado (o cron não tenta —
                        seria recusado).
                      </p>
                    </Recolhivel>

                    {estado === 'ok' && (semCodigoSga > 0 || seeds > 0) && (
                      <Nota variante="atencao">
                        {semCodigoSga > 0 && (
                          <span className="tw-block">
                            <strong>
                              {semCodigoSga} modelo(s) ativo(s) sem código FIPE do SGA
                            </strong>{' '}
                            — adesões desses modelos não subirão ao SGA até o código ser preenchido.
                          </span>
                        )}
                        {seeds > 0 && (
                          <span className="tw-block">
                            {seeds} modelo(s) ainda com os valores da{' '}
                            <strong>carga inicial</strong> (aproximados). Editar um deles o marca
                            como revisado.
                          </span>
                        )}
                      </Nota>
                    )}

                    <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
                      <SearchInput
                        key={chaveBusca}
                        valorInicial={busca}
                        placeholder="Buscar por marca ou modelo"
                        rotuloAcessivel="Buscar motocicleta elétrica"
                        className="tw-min-w-0 tw-flex-1 tw-basis-64"
                        aoBuscar={setBusca}
                      />

                      <Field rotulo="Marca" className="tw-w-full sm:tw-w-56" campo="marca">
                        <Select
                          opcoes={[
                            { valor: '', rotulo: 'Todas as marcas' },
                            ...marcas.map((m) => ({
                              valor: m.id,
                              rotulo: `${m.nome} (${m.totalModelos})`,
                            })),
                          ]}
                          placeholder=""
                          value={marcaFiltro}
                          onChange={(evento) => setMarcaFiltro(evento.target.value)}
                        />
                      </Field>

                      <div className="tw-pb-3">
                        <Checkbox
                          rotulo="Mostrar inativos"
                          checked={mostrarInativos}
                          onChange={setMostrarInativos}
                        />
                      </div>
                    </div>

                    {painelModelos()}
                  </>
                ) : (
                  painelMarcas()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalConfirmacao
        aberto={confirmacao !== null}
        titulo={conf.titulo}
        mensagem={conf.mensagem}
        detalhe={conf.detalhe}
        rotuloConfirmar={confirmacao?.tipo.startsWith('REATIVAR') ? 'Reativar' : 'Inativar'}
        destrutivo={confirmacao?.tipo.startsWith('INATIVAR')}
        processando={processando}
        onConfirmar={() => void confirmar()}
        onCancelar={() => setConfirmacao(null)}
      />
    </ShellSaac>
  );
};

export default BikesEletricasPage;
